<template>
  <layout-selection-toolbar-teleport>
    <ul class="list-unstyled flex gap-0 lg:gap-2 items-center">
      <li v-if="hasMerge">
        <file-selection-toolbar-merge-button :context="context" :context-type="contextType" />
      </li>
      <li v-if="hasOrganize">
        <u-dropdown :items="organizeItems">
          <core-selection-toolbar-button icon="i-ri-stack-line">Organize</core-selection-toolbar-button>
        </u-dropdown>
      </li>
      <li v-for="action in allActions">
        <core-selection-toolbar-button :icon="action.icon" @click="action.handler">{{action.label}}</core-selection-toolbar-button>
      </li>
      <li v-if="hasRemove">
        <u-dropdown :items="removeItems">
          <core-selection-toolbar-button icon="i-ri-close-circle-line">Remove</core-selection-toolbar-button>
        </u-dropdown>
      </li>
    </ul>
  </layout-selection-toolbar-teleport>
</template>

<script setup>
  import {DECLUTTER_MAX_FILE_COUNT} from '~/constants/declutter.js';

  const props = defineProps({
    actions: Array,
    context: Object,
    contextType: String,
    userId: String
  });

  const fileActions = useFileActions();
  const selection = useSelection();
  const actionsEnum = fileActions.selectionToolbarActions;

  const hasOrganize = props.actions.includes(actionsEnum.organize);
  const hasRemove = props.actions.includes(actionsEnum.removeFromContainer);
  const hasMoveToAlbum = props.actions.includes(actionsEnum.moveToAlbum);
  const hasMerge = props.actions.includes(actionsEnum.merge);

  const allActions = computed(() => [
    {
      label: 'Share',
      icon: COMMON_ICONS.share,
      handler: share,
      included: props.actions.includes(actionsEnum.share)
    },
    {
      label: 'Download',
      icon: COMMON_ICONS.download,
      handler: download,
      included: props.actions.includes(actionsEnum.download)
    },
    {
      label: 'Make Cover',
      icon: COMMON_ICONS.makeCover,
      handler: makeCover,
      included: props.actions.includes(actionsEnum.makeCover) && selection.count.value === 1
    },
    {
      label: 'Delete',
      icon: COMMON_ICONS.delete,
      handler: deleteFiles,
      included: props.actions.includes(actionsEnum.delete)
    },
    {
      label: 'Copy',
      icon: 'i-ri-file-copy-2-line',
      handler: copy,
      included: props.actions.includes(actionsEnum.copyToMyAccount)
    },
    {
      label: 'Restore',
      icon: 'i-ri-arrow-go-back-line',
      handler: restoreToLibrary,
      included: props.actions.includes(actionsEnum.restoreToLibrary)
    },
    {
      label: 'Delete Permanently',
      icon: 'i-ri-delete-bin-6-line',
      handler: purge,
      included: props.actions.includes(actionsEnum.purge)
    }
  ].filter(action => action.included));

  const organizeItems = computed(() => [
    [
      {
        label: 'Add to Album',
        icon: 'i-ri-folder-add-line',
        click: addToAlbum,
        included: true
      },

      {
        label: 'Move to Album',
        icon: 'i-ri-folder-transfer-line',
        click: moveToAlbum,
        included: hasMoveToAlbum
      },
      {
        label: 'Add Tags',
        icon: COMMON_ICONS.tag,
        click: addTags,
        included: true
      },
      {
        label: 'Add People',
        icon: COMMON_ICONS.person,
        click: addToPeople,
        included: true
      }
    ].filter(action => action.included),
    [
      {
        label: 'Add to Favorites',
        icon: COMMON_ICONS.favoriteFill,
        click: addToFavorites,
        included: true
      },
      {
        label: 'Remove from Favorites',
        icon: COMMON_ICONS.favoriteLine,
        click: removeFromFavorites,
        included: true
      }
    ].filter(action => action.included),
    [
      {
        label: 'Bulk Organize',
        icon: 'i-ri-stack-line',
        click: bulkOrganize,
        included: true
      },
      {
        label: 'Bulk Photo Rotation',
        icon: 'i-ri-clockwise-line',
        click: bulkRotate,
        included: selection.selected.value.some(f => f.is.photo)
      },
      {
        label: 'Compare',
        icon: 'i-ri-multi-image-line',
        click: compare,
        included: true
      }
    ].filter(action => action.included)
  ]);

  const removeItems = [
    [
      {
        label: `Remove from ${FILE_CONTEXTS_FRIENDLY[props.contextType]}`,
        icon: 'i-ri-close-circle-line',
        click: removeFilesFromContext
      },
      {
        label: 'Delete Files',
        icon: COMMON_ICONS.delete,
        click: deleteFiles
      }
    ]
  ];

  async function addToAlbum() {
    await fileActions.addMoveFilesToAlbum({files: selection.selected.value});
    selection.clear();
  }

  async function moveToAlbum() {
    await fileActions.addMoveFilesToAlbum({files: selection.selected.value, fromAlbumId: props.context.id});
    selection.clear();
  }

  async function addTags() {
    await fileActions.addTagsToFiles({files: selection.selected.value});
    selection.clear();
  }

  async function addToPeople() {
    await fileActions.addFilesToPeople({files: selection.selected.value});
    selection.clear();
  }

  async function addToFavorites() {
    await useFilesStore().updateFilesFavorites({files: selection.selected.value});
    useSuccessToast().add({
      description: `The selected ${selection.selected.value.length} files were successfully added to your Favorites.`
    });

    selection.clear();
  }

  async function removeFromFavorites() {
    await useFilesStore().updateFilesFavorites({files: selection.selected.value, favorite: false});
    useSuccessToast().add({
      description: `The selected ${selection.selected.value.length} files were successfully removed from your Favorites.`
    });

    selection.clear();
  }

  async function bulkOrganize() {
    await fileActions.bulkOrganize({files: selection.selected.value});
    selection.clear();
  }

  async function bulkRotate() {
    const selectedPhotos = computed(() => selection.selected.value.filter(f => f.is.photo));

    if (selectedPhotos.value.length !== selection.selected.value.length) {
      useInfoToast().add({
        description: `Only Photos can be rotated. Non-photos were removed from your selection.`
      });
    }

    await fileActions.bulkRotate({files: selection.selected.value});
    selection.clear();
  }

  async function share() {
    fileActions.share({files: selection.selected.value});
  }

  async function download() {
    await fileActions.downloadFiles({files: selection.selected.value, userId: props.userId});
  }

  async function deleteFiles() {
    await fileActions.deleteFiles({files: selection.selected.value});
    selection.clear();
  }

  async function removeFilesFromContext() {
    await fileActions.removeFilesFromContext({files: selection.selected.value, context: props.context, contextType: props.contextType});
    selection.clear();
  }

  async function makeCover() {
    const actions = {
      'album': fileActions.setFileAsAlbumCover,
      'tag': fileActions.setFileAsTagCover,
      'person': fileActions.setFileAsPersonCover
    };

    const action = actions[props.contextType];

    if (action) {
      await action({
        file: selection.selected.value[0],
        [props.contextType]: props.context
      });
    }
    selection.clear();
  }

  async function copy() {
    await fileActions.copyFilesToCurrentUserAccount({files: selection.selected.value, userId: props.userId});
    selection.clear();
  }

  async function restoreToLibrary() {
    await fileActions.restoreFiles({files: selection.selected.value});
    selection.clear();
  }

  async function purge() {
    await fileActions.purgeFiles({files: selection.selected.value});
    selection.clear();
  }

  async function compare() {
    const selectedPhotos = computed(() => selection.selected.value.filter(f => f.is.photo));

    if (!selectedPhotos.value.length) {
      useErrorToast().add({
        description: `None of the selected items are photos. Please select photos to compare.`
      });
      return;
    }

    if (selectedPhotos.value.length !== selection.selected.value.length && selectedPhotos.value.length > DECLUTTER_MAX_FILE_COUNT) {
      useInfoToast().add({
        description: `Some selected files aren't photos, and you selected more than ${DECLUTTER_MAX_FILE_COUNT} items. We've kept the first ${DECLUTTER_MAX_FILE_COUNT} photos for comparison.`
      });
    } else if (selectedPhotos.value.length !== selection.selected.value.length) {
      useInfoToast().add({
        description: `Only Photos can be compared. Non-photos were removed from your selection.`
      });
    } else if (selectedPhotos.value.length > DECLUTTER_MAX_FILE_COUNT) {
      useInfoToast().add({
        description: `Comparison is limited to ${DECLUTTER_MAX_FILE_COUNT} photos. Only the first ${DECLUTTER_MAX_FILE_COUNT} selected are being compared.`
      });
    }

    const group = {
      id: 'selection-compare',
      files: selectedPhotos.value.slice(0, DECLUTTER_MAX_FILE_COUNT)
    }

    let hasDeletedFiles = false;

    const modal = useFModal({
      id: 'declutter-compare-modal',
      clickToClose: false,
      showClose: false,
      escToClose: false,
      size: 'fullscreen',
      component: (process.client ? defineAsyncComponent(() => import('~/components/file/declutter-compare-modal.vue')) : {}),
      props: {
        group,
        context: props.context,
        contextType: props.contextType
      },
      onClose() {
        if (hasDeletedFiles) {
          selection.clear();
        }
      },
      eventHandlers: {
        filesDeleted({deletedFiles}) {
          hasDeletedFiles = true;
          const deletedIds = deletedFiles.map(f => f.id);
          selection.update({unselect: deletedFiles});
          group.files = group.files.filter(f => !deletedIds.includes(f.id));

          if (!group.files.length) {
            modal.close();
          }
        }
      }
    });

    modal.open();
  }

</script>
